// src/components/TuneRecorder.js
import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import dexie from '../offlineDB';
import { syncTuneRecordings } from '../utils/syncTuneRecordings';
import { useRecording } from '../RecordingContext';
import { useLocation } from 'react-router-dom';

const TuneRecorder = ({
  tune,
  user,
  recordings = [],
  onUpdateRecordings,
  onRecordingSaved,
  buttonStyle = {},
  inputStyle = {},
  containerStyle = {},
  compact = false
}) => {
  const [label, setLabel] = useState('');
  const audioRef = useRef(null);
  const saveHandledRef = useRef(false);

  const {
    isRecording,
    startRecording,
    stopRecording,
    originPath,
    setOriginPath,
    elapsedTime,
  } = useRecording();

  const location = useLocation();

  useEffect(() => {
    // When mounted, set originPath if not set
    if (isRecording && !originPath) {
      setOriginPath(location.pathname);
    }
  }, [isRecording, originPath, location.pathname, setOriginPath]);

  const handleStartRecording = async () => {
    await startRecording(location.pathname);
  };

  const handleStopRecording = async () => {
    try {
      const result = await stopRecording();
      if (!result) return;

      const { blob, url, mimeType } = result;

      console.log('[TuneRecorder] BLOB SIZE:', blob?.size);

      if (!blob || blob.size === 0) {
        alert("Recording failed — no audio was captured.");
        return;
      }

      const id = uuidv4();
      const createdAt = new Date().toISOString();
      saveHandledRef.current = false;

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onloadedmetadata = async () => {
        if (saveHandledRef.current) return;
        saveHandledRef.current = true;
        let duration = Math.round(audio.duration);
        if (!isFinite(duration) || isNaN(duration)) {
          console.warn('[TuneRecorder] Invalid duration detected, falling back to null');
          duration = null;
        }

        const newRecording = {
          id,
          label,
          url: null,
          duration,
          createdAt,
          storagePath: null,
          mimeType: mimeType || blob.type || 'audio/webm',
          localBlob: blob
        };

        let updatedRecordings = [];

        const baseRecordings = Array.isArray(tune?.recordings) && tune.recordings.length > 0
          ? tune.recordings
          : (recordings || []);

        if (tune?.id) {
          updatedRecordings = [...baseRecordings, newRecording];
          // Dedupe by id in case metadata callback races.
          updatedRecordings = Array.from(new Map(updatedRecordings.map(r => [r.id, r])).values());
          await dexie.tunes.update(tune.id, { recordings: updatedRecordings });
          try {
            await syncTuneRecordings({ ...tune, recordings: updatedRecordings }, user);
          } catch (syncErr) {
            console.warn('Recording saved locally; sync failed:', syncErr);
          }
        } else {
          updatedRecordings = [...baseRecordings, newRecording];
          updatedRecordings = Array.from(new Map(updatedRecordings.map(r => [r.id, r])).values());
        }

        if (onUpdateRecordings) {
          onUpdateRecordings(updatedRecordings);
        }

        if (onRecordingSaved) {
          onRecordingSaved(newRecording);
        }

        setLabel('');
      };

      // Sometimes on iOS, metadata doesn't fire — fallback handler
      setTimeout(() => {
        if (!saveHandledRef.current && (!audioRef.current?.duration || !isFinite(audioRef.current.duration))) {
          console.warn('[TuneRecorder] Metadata load timeout — using fallback path');
          audio.onloadedmetadata();
        }
      }, 2000);

    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
  };

  const formatElapsedTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const recorderInputStyle = compact
    ? {
      fontSize: '0.8rem',
      height: '26px',
      padding: '0.08rem 0.38rem',
      border: '1px solid color-mix(in srgb, var(--input-border, #666) 70%, transparent)',
      borderRadius: '8px',
      backgroundColor: 'var(--input-bg)',
      color: 'var(--input-text, var(--text-primary))',
      ...inputStyle,
    }
    : {
      ...inputStyle,
    };

  const recorderButtonStyle = {
    fontSize: compact ? '0.78rem' : '0.8rem',
    height: '26px',
    padding: compact ? '0.06rem 0.48rem' : '0.08rem 0.5rem',
    backgroundColor: '#c92a2a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    lineHeight: 1.1,
    ...buttonStyle,
  };

  return (
<div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', ...containerStyle }}>
<input
  type="text"
  className={compact ? undefined : 'search-input'}
  placeholder="Recording title"
  value={label}
  onChange={e => setLabel(e.target.value)}
  disabled={isRecording}
  style={{
    ...recorderInputStyle,
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
  }}
/>
      {!isRecording ? (
<button className="btn-subtle" onClick={handleStartRecording} style={{ ...recorderButtonStyle, whiteSpace: 'nowrap' }}>
Record</button>
      ) : (
       <button className="btn-subtle" onClick={handleStopRecording} style={{ ...recorderButtonStyle, backgroundColor: '#e03131', color: '#fff', whiteSpace: 'nowrap' }}>
          {elapsedTime > 0 ? `Stop ${formatElapsedTime(elapsedTime)}` : 'Stop'}
        </button>
      )}
    </div>
  );
};

export default TuneRecorder;
